import { useId, useState, type ReactNode } from 'react'
import { Building2, ChevronDown } from 'lucide-react'
import { InlineFieldGroup, InlineFieldInput, type InlineGroupField } from '@/components/inline-field-group'
import { cn } from '@/lib/utils'
import type { Address } from '../api/types'
import { addressSummary } from './address'

export function Field({ label, children }: { label: string; children: (id: string) => ReactNode }) {
  const id = useId()
  return (
    <div className="grid gap-1.5">
      <label className="text-xs font-medium text-muted-foreground" htmlFor={id}>
        {label}
      </label>
      {children(id)}
    </div>
  )
}

type AddressFieldsProps = {
  value: Address
  disabled?: boolean
  onChange: (value: Address) => void
  /**
   * Saves a stored address: the panel then shows it and edits it in place, all fields together
   * (USA-1580). Without it the fields are always open, for a new quote's addresses.
   */
  onCommit?: (value: Address) => void
  actionLabel?: string
  onAction?: () => void
  /** Only the street, city, state and ZIP: where something goes, not who to call (USA-1543). */
  streetOnly?: boolean
}

export function AddressFields({ value, disabled, onChange, onCommit, actionLabel, onAction, streetOnly }: AddressFieldsProps) {
  const field = (key: keyof Address, label: string, extra: Partial<React.ComponentProps<'input'>> = {}) => (
    <Field label={label}>
      {(id) => (
        <InlineFieldInput
          disabled={disabled}
          id={id}
          onBlur={() => onCommit?.(value)}
          onChange={(event) => onChange({ ...value, [key]: key === 'country' ? event.target.value.toUpperCase() : event.target.value })}
          value={value[key]}
          {...extra}
        />
      )}
    </Field>
  )
  return (
    <div className="grid content-start gap-3">
      {actionLabel ? (
        <button
          className="ml-auto rounded text-xs font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
          disabled={disabled}
          onClick={onAction}
          type="button"
        >
          {actionLabel}
        </button>
      ) : null}
      {streetOnly ? null : (
        <>
          {field('name', 'Name')}
          {field('contact_name', 'Contact')}
          <div className="grid gap-3 sm:grid-cols-2">
            {field('email', 'Email', { type: 'email' })}
            {field('phone', 'Phone', { type: 'tel' })}
          </div>
        </>
      )}
      {field('line1', 'Address 1')}
      {field('line2', 'Address 2')}
      {streetOnly ? (
        <div className="grid grid-cols-[minmax(0,1fr)_72px_96px] gap-3">
          {field('city', 'City')}
          {field('state', 'State', { maxLength: 100 })}
          {field('postal_code', 'ZIP', { inputMode: 'numeric' })}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-[minmax(0,1fr)_88px] gap-3">
            {field('city', 'City')}
            {field('state', 'State', { maxLength: 100 })}
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_88px] gap-3">
            {field('postal_code', 'ZIP')}
            {field('country', 'Country', { maxLength: 2 })}
          </div>
        </>
      )}
    </div>
  )
}

type AddressKey = keyof Address & string

const ADDRESS_FIELDS: ReadonlyArray<InlineGroupField<AddressKey>> = [
  { key: 'name', label: 'Name', autoComplete: 'organization' },
  { key: 'contact_name', label: 'Contact', autoComplete: 'name' },
  { key: 'email', label: 'Email', inputMode: 'email', autoComplete: 'email', className: 'col-span-6 sm:col-span-3' },
  { key: 'phone', label: 'Phone', inputMode: 'tel', autoComplete: 'tel', className: 'col-span-6 sm:col-span-3' },
  { key: 'line1', label: 'Address 1', autoComplete: 'address-line1' },
  { key: 'line2', label: 'Address 2', autoComplete: 'address-line2' },
  { key: 'city', label: 'City', autoComplete: 'address-level2', className: 'col-span-4' },
  { key: 'state', label: 'State', autoComplete: 'address-level1', className: 'col-span-2' },
  { key: 'postal_code', label: 'ZIP', autoComplete: 'postal-code', className: 'col-span-4' },
  { key: 'country', label: 'Country', autoComplete: 'country', className: 'col-span-2' },
]

/** The stored address as it reads when not being edited. */
function AddressBlock({ value }: { value: Address }) {
  const locality = [[value.city, value.state].filter(Boolean).join(', '), value.postal_code].filter(Boolean).join(' ')
  const reach = [value.email, value.phone].filter(Boolean).join(' · ')
  const lines = [value.contact_name, reach, value.line1, value.line2, locality, value.country && value.country !== 'US' ? value.country : '']
  if (!value.name && !lines.some(Boolean)) return <span className="text-sm text-muted-foreground">Add address</span>
  return (
    <span className="grid gap-0.5 break-words text-sm">
      {value.name ? <span className="font-medium">{value.name}</span> : null}
      {lines.filter(Boolean).map((line, index) => (
        <span key={index}>{line}</span>
      ))}
    </span>
  )
}

/** A stored address: read as a block, clicked to edit every field in place, saved together. */
function StoredAddress({
  title,
  value,
  disabled,
  editing,
  onEditingChange,
  onChange,
  onCommit,
}: {
  title: string
  value: Address
  disabled?: boolean
  editing: boolean
  onEditingChange: (editing: boolean) => void
  onChange: (value: Address) => void
  onCommit: (value: Address) => void
}) {
  return (
    <InlineFieldGroup
      disabled={disabled}
      editing={editing}
      fields={ADDRESS_FIELDS}
      // A suggestion or "Use customer" replaces the address underneath an open edit; start it again.
      key={JSON.stringify(value)}
      label={`${title} address`}
      onEditingChange={onEditingChange}
      onSave={(draft) => {
        // The old always-open boxes capped State at 100 characters and Country at two, upper case.
        const next = { ...value, ...draft, state: draft.state.slice(0, 100), country: draft.country.slice(0, 2).toUpperCase() }
        onEditingChange(false)
        onChange(next)
        onCommit(next)
      }}
      values={value}
    >
      <AddressBlock value={value} />
    </InlineFieldGroup>
  )
}

/**
 * A collapsed address showing its one-line summary; opens to the full address. `status` is a row
 * under it that stays visible while collapsed (the Customer stage's address check). A stored
 * address (`onCommit`) opens to the address itself, edited in place; a new quote's opens to its
 * fields.
 */
export function AddressPanel({
  title,
  status,
  id,
  open,
  ...props
}: AddressFieldsProps & {
  title: string
  status?: ReactNode
  /** Anchor a blocker links to (USA-1140). */
  id?: string
  /** Opened and outlined because a blocker sent the user here; a stored address opens to edit. */
  open?: boolean
}) {
  const { value, disabled, onChange, onCommit, actionLabel, onAction } = props
  const [editing, setEditing] = useState(Boolean(open))
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setEditing(true)
  }
  return (
    <div className={cn('scroll-mt-16 rounded-xl border bg-card', open && 'ring-2 ring-amber-500/60')} id={id}>
      <details className="group" open={open || undefined}>
        <summary className="flex min-h-14 cursor-pointer list-none items-center gap-3 rounded-xl px-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
          <span className="grid size-8 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
            <Building2 aria-hidden="true" className="size-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">{title}</span>
            <span className="mt-0.5 block truncate text-xs text-muted-foreground">{addressSummary(props.value)}</span>
          </span>
          <ChevronDown aria-hidden="true" className="size-4 text-muted-foreground transition-transform group-open:rotate-180" />
        </summary>
        <div className="border-t p-4">
          {onCommit ? (
            <div className="grid content-start gap-3">
              {actionLabel ? (
                <button
                  className="ml-auto rounded text-xs font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
                  disabled={disabled}
                  onClick={() => {
                    setEditing(false)
                    onAction?.()
                  }}
                  type="button"
                >
                  {actionLabel}
                </button>
              ) : null}
              <StoredAddress
                disabled={disabled}
                editing={editing}
                onChange={onChange}
                onCommit={onCommit}
                onEditingChange={setEditing}
                title={title}
                value={value}
              />
            </div>
          ) : (
            <AddressFields {...props} />
          )}
        </div>
      </details>
      {status ? <div className="border-t px-4 py-2.5">{status}</div> : null}
    </div>
  )
}
